import { env } from "@acme/env/client";
import type { Messaging } from "firebase/messaging";
import { getToken, onMessage } from "firebase/messaging";

const VAPID_KEY = env.PUBLIC_FIREBASE_VAPID_KEY;

export interface NotificationPayload
{
  title?: string;
  body?: string;
  image?: string;
}

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> =>
{
  if (!("Notification" in window))
  {
    console.log("This browser does not support notifications");
    return "denied";
  }

  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Get FCM token for the device
 * Note: Assumes permission is already granted - call requestNotificationPermission() first
 */
export const getFCMToken = async (messaging: Messaging | null): Promise<string | null> =>
{
  if (!messaging)
  {
    console.log("Messaging not initialized");
    return null;
  }

  if (Notification.permission !== "granted")
  {
    console.log("Notification permission not granted - cannot get token");
    return null;
  }

  try
  {
    // Get the service worker registration
    const registration = await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration, // ← WICHTIG: Service Worker angeben!
    });

    if (token)
    {
      console.log("FCM Token:", token);
      return token;
    }
    else
    {
      console.log("No registration token available");
      return null;
    }
  }
  catch (err)
  {
    console.error("An error occurred while retrieving token:", err);
    return null;
  }
};

/**
 * Setup foreground message listener
 */
export const onForegroundMessage = (messaging: Messaging | null, callback: (payload: NotificationPayload) => void) =>
{
  if (!messaging)
  {
    console.log("Messaging not initialized");
    return () =>
    {};
  }

  return onMessage(messaging, (payload) =>
  {
    console.log("Foreground message received:", payload);

    const notificationData: NotificationPayload = {
      title: payload.notification?.title,
      body: payload.notification?.body,
      image: payload.notification?.image,
    };

    callback(notificationData);
  });
};

/**
 * Display a notification (for foreground messages)
 */
export const showNotification = async (payload: NotificationPayload) =>
{
  if (!("Notification" in window))
  {
    console.log("Notifications not supported");
    return;
  }

  if (Notification.permission !== "granted")
  {
    console.log("Notification permission not granted");
    return;
  }

  try
  {
    // Prefer Service Worker notification API when available (better browser support)
    if ("serviceWorker" in navigator)
    {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(payload.title || "New Message", {
        body: payload.body,
        icon: payload.image || "/logo.png",
        badge: "/logo.png",
        tag: "fcm-notification",
        requireInteraction: false,
      });
      console.log("✅ Notification displayed via Service Worker");
    }
    else
    {
      // Fallback to basic Notification API
      new Notification(payload.title || "New Message", {
        body: payload.body,
        icon: payload.image || "/logo.png",
        badge: "/logo.png",
      });
      console.log("✅ Notification displayed via Notification API");
    }
  }
  catch (err)
  {
    console.error("❌ Failed to show notification:", err);
  }
};
